// @ai-generated 2026-09-04T08:07:37+00:00 by dimitri.driediger@synqony.com using Claude Opus 5
import { type ApiContainerType, type ApiService } from '@Model';
import {
    Button,
    FloatingCard,
    Grid,
    GridColumns,
    iconSizeSm,
    InlineBanner,
    LabeledDiv,
    type ILinkInputProperties,
    LinkedField,
    NumberStepper,
    TextInput,
    useTranslate,
    joinClassNames,
} from '@synqo/frontend-component-library';
import { IconX } from '@tabler/icons-react';
import {
    type ContainerPosition,
    type ContainerPositionOrigin,
    emptyPosition,
    maxCountFor,
    MIN_COUNT,
} from '../../containerOrder';
import classes from '../ContainerSteps.module.css';
import { ContentField } from './ContentField';

interface ContainerPositionFormProps {
    /** Which kind of container the form describes, which decides its count limit. */
    origin: Extract<ContainerPositionOrigin, 'described' | 'placement'>;
    /** Absent where the step title already says what the form is. */
    title?: string;
    /** Sits under the title and says what the form is for. */
    subtitle?: string;
    /** Explains what happens with the entered data, e.g. that the dispatcher checks it. */
    hint?: string;
    /** The number the customer states for a container we have no record of. Absent for a placement. */
    onContainerNumber?: (containerNumber: string) => void;
    /** Asks where the container stands, or is to stand, under the given label. */
    location?: {
        label: string;
        onChange: (locationHint: string) => void;
    };
    /** The order's container as it stands; absent while nothing has been named yet. */
    position: ContainerPosition | undefined;
    containerTypeField: ILinkInputProperties<ApiContainerType>;
    contentField: ILinkInputProperties<ApiService>;
    /** The waste code of the chosen content, empty while none is known. */
    avvCode: string;
    /** Why "Weiter" did not go on, shown at the content field. */
    contentError?: string;
    onCount: (count: number) => void;
    /** Drops what the form holds and closes it. Absent where the form is the step itself. */
    onDiscard?: () => void;
}

/**
 * The fields describing the order's one container: the one the customer wants placed, and the one
 * they know of but our records do not.
 */
export function ContainerPositionForm({
    origin,
    title,
    subtitle,
    hint,
    onContainerNumber,
    location,
    position,
    containerTypeField,
    contentField,
    avvCode,
    contentError,
    onCount,
    onDiscard,
}: ContainerPositionFormProps) {
    const i18n = useTranslate('');

    const current = position ?? emptyPosition(origin);
    const hasType = !!current.containerTypeId;
    const withCount = hasType && current.usesIndividualManagement === false;

    return (
        <FloatingCard
            title={title ?? ''}
            separateHeader={false}
            collapsible={false}
            additionalHeaderItems={
                onDiscard ? (
                    <Button
                        appearance="transparent"
                        type="square"
                        size="xs"
                        className={classes.formClose}
                        onClick={onDiscard}
                        label={i18n('Verwerfen')}
                    >
                        <IconX size={iconSizeSm} />
                    </Button>
                ) : undefined
            }
            className={joinClassNames(classes.cardHeaderStretch)}
        >
            {subtitle && <span className={classes.formSubtitle}>{subtitle}</span>}

            <Grid columns={12}>
                <GridColumns spans={withCount ? [9, 3] : [12]}>
                    <LinkedField<ApiContainerType>
                        {...containerTypeField}
                        label={i18n('Containertyp')}
                        placeholder={i18n('Containertyp wählen …')}
                    />

                    {withCount && (
                        <LabeledDiv label={i18n('Anzahl')} className={classes.countField}>
                            <NumberStepper
                                name="container_count"
                                ariaLabel={current.containerTypeLabel || i18n('Container')}
                                value={current.count}
                                min={MIN_COUNT}
                                max={maxCountFor(current)}
                                editable
                                fullWidth
                                onChange={(count) => onCount(count)}
                            />
                        </LabeledDiv>
                    )}
                </GridColumns>
            </Grid>

            {(onContainerNumber || location) && (
                <Grid columns={12}>
                    <GridColumns spans={onContainerNumber && location ? [6, 6] : [12]}>
                        {onContainerNumber && (
                            <TextInput
                                name="container_number"
                                label={`${i18n('Containernummer')} (${i18n('optional')})`}
                                placeholder={i18n('z. B. 4791')}
                                locked={!hasType}
                                value={current.containerNumber}
                                onChange={() => true}
                                onConfirm={(value) => {
                                    onContainerNumber(value);
                                    return true;
                                }}
                            />
                        )}
                        {location && (
                            <TextInput
                                name="location_hint"
                                label={`${location.label} (${i18n('optional')})`}
                                placeholder={i18n('z. B. hinten an Tor 3')}
                                locked={!hasType}
                                value={current.locationHint}
                                onChange={() => true}
                                onConfirm={(value) => {
                                    location.onChange(value);
                                    return true;
                                }}
                            />
                        )}
                    </GridColumns>
                </Grid>
            )}

            <ContentField contentField={contentField} avvCode={avvCode} error={contentError} locked={!hasType} />

            {hint && <InlineBanner size="sm" variant="warning" title={hint} />}
        </FloatingCard>
    );
}
